import {
  formatBoardDate,
  formatShareAssists,
  formatTimeCompact,
  type GameType,
  type PlayResult,
  type SimpleDate,
} from '@puzzlio/core';
import { GAME_META, PLAYABLE_GAMES } from './catalog';

/*
  The day-results page's catalog-aware half: its share block, and how far back its day arrows go.
  The per-puzzle wording both draw on lives in `@puzzlio/core/resultText`.
*/

/** One completed puzzle, as the day-results page reports it. */
export type CompletedPuzzle = { gameType: GameType; result: PlayResult };

/** The first date any game had a board. Earlier days have nothing to show at all. */
export const EARLIEST_RESULTS_DATE: SimpleDate = PLAYABLE_GAMES.map((game) => game.earliestDate).sort()[0];

/**
 * The day's solves added up: the times and assists of every completed puzzle, which are the numbers its
 * rows show. Undefined until there are two to add, since a total of one is just that puzzle again.
 */
export function dayTotal(completed: CompletedPuzzle[]): PlayResult | undefined {
  if (completed.length < 2) return undefined;
  return completed.reduce(
    (total, { result }) => ({
      durationSec: total.durationSec + result.durationSec,
      hintsUsed: total.hintsUsed + result.hintsUsed,
      checksUsed: total.checksUsed + result.checksUsed,
    }),
    { durationSec: 0, hintsUsed: 0, checksUsed: 0 }
  );
}

/**
 * A total's assists on screen, as a row's are but for the day: "💡3 Hints and checks". "🎯 Perfect day"
 * only once every puzzle of the day is solved without one; short of that, nothing.
 */
export function formatDayAssists(total: PlayResult, allSolved: boolean): string {
  const assists = total.hintsUsed + total.checksUsed;
  if (assists > 0) return `💡${assists} Hints and checks`;
  return allSolved ? '🎯 Perfect day' : '';
}

/**
 * A whole day's solves as one shareable block — the day-level counterpart to the per-puzzle share.
 *
 * Only completed puzzles appear, out of the day's `puzzleCount`. Someone who shares mid-day is reporting what they've done so far, not
 * publishing a list of what they haven't got to; an unfinished game is simply absent.
 */
export function formatDayShareText(date: SimpleDate, completed: CompletedPuzzle[], puzzleCount: number): string {
  const lines = completed.map(({ gameType, result }) => {
    const { emoji, label } = GAME_META[gameType];
    const assists = formatShareAssists(result.hintsUsed + result.checksUsed);
    return `${emoji} ${label} ${formatTimeCompact(result.durationSec)} ${assists}`;
  });

  const total = dayTotal(completed);
  if (total) {
    const assists = total.hintsUsed + total.checksUsed;
    const assistsText =
      assists > 0 ? ` 💡 ${assists} hints/checks` : completed.length === puzzleCount ? ' 🎯 a perfect day' : '';
    lines.push('', `Total ${formatTimeCompact(total.durationSec)}${assistsText}`);
  }

  return `Inkwell Games\n${formatBoardDate(date)}\n\n${lines.join('\n')}`;
}
